<template>
  <section class="custom-wrapper">
    <div class="row mt-2 mb-3">
      <div class="col-12">
        <h5 class="mb-3">Service Method Configuration</h5>
        <p class="text-muted mb-4">Select the service delivery methods your business offers to customers.</p>
        
        <div class="service-methods-container">
          <div 
            v-for="method in serviceMethodOptions" 
            :key="method.id" 
            class="service-method-item mb-3"
          >
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                :id="method.id"
                :value="method.id"
                v-model="selectedServiceMethods"
                @change="changed"
              >
              <label class="form-check-label" :for="method.id">
                <strong>{{ method.name }}</strong>
              </label>
            </div>
            <div class="method-description">
              {{ method.description }}
            </div>
            
            <!-- Custom text input for "Other" option -->
            <div v-if="method.name === 'Other' && isOtherSelected" class="custom-other-input mt-3">
              <div class="row">
                <div class="col-md-6">
                  <label for="customServiceName" class="form-label">Service Method Name</label>
                  <input 
                    type="text" 
                    id="customServiceName"
                    class="form-control" 
                    v-model="customServiceName"
                    @input="updateCustomService"
                    placeholder="Enter custom service type name"
                  >
                </div>
                <div class="col-md-6">
                  <label for="customServiceDescription" class="form-label">Description</label>
                  <textarea 
                    id="customServiceDescription"
                    class="form-control" 
                    rows="2"
                    v-model="customServiceDescription"
                    @input="updateCustomService"
                    placeholder="Describe this service type"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="selectedServiceMethods.length === 0" class="alert alert-info mt-3">
          <i class="fas fa-info-circle me-2"></i>
          Please select at least one service method to indicate how you deliver services to your customers.
        </div>
        
        <div v-else class="alert alert-success mt-3">
          <i class="fas fa-check-circle me-2"></i>
          You have selected {{ selectedServiceMethods.length }} service method{{ selectedServiceMethods.length > 1 ? 's' : '' }}.
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { locationStore } from '@/stores/location'

export default {
  name: 'ServiceMethod',
  props: {
    firm: Object
  },
  emits: ['changed'],
  data() {
    return {
      customServiceName: '',
      customServiceDescription: ''
    }
  },
  watch: {
    // Watch for changes in profile data to initialize custom service fields
    'profileData.customServiceMethod': {
      handler(newValue) {
        if (newValue) {
          this.customServiceName = newValue.name || ''
          this.customServiceDescription = newValue.description || ''
        } else {
          this.customServiceName = ''
          this.customServiceDescription = ''
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState(locationStore, ['profileData', 'serviceMethodOptions', 'location']),
    launchpadFirmId() {
      return this.location?.firmId || this.launchpadFirmId
    },
    selectedServiceMethods: {
      get() {
        return this.profileData?.serviceMethods || []
      },
      set(value) {
        this.updateProfileField('serviceMethods', value, this.launchpadFirmId)
        // If "Other" is deselected, clear the custom text
        if (!this.isOtherSelected) {
          this.customServiceName = ''
          this.customServiceDescription = ''
          this.updateProfileField('customServiceMethod', null, this.launchpadFirmId)
        }
      }
    },
    isOtherSelected() {
      const otherMethod = this.serviceMethodOptions.find(method => method.name === 'Other')
      return otherMethod && this.selectedServiceMethods.includes(otherMethod.id)
    }
  },
  methods: {
    ...mapActions(locationStore, ['updateProfileField', 'saveProfileData']),
    changed() {
      this.$emit('changed')
    },
    updateCustomService() {
      const customServiceMethod = {
        name: this.customServiceName,
        description: this.customServiceDescription
      }
      this.updateProfileField('customServiceMethod', customServiceMethod, this.launchpadFirmId)
      this.changed()
    },
    async save() {
      if (this.launchpadFirmId) {
        await this.saveProfileData(this.launchpadFirmId)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-wrapper {
  width: 100%;
  position: relative;
  padding: 20px;
}

h5 {
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.service-methods-container {
  margin-top: 1rem;
}

.service-method-item {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    border-color: #80bdff;
    background-color: #e7f3ff;
  }
}

.form-check {
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-top: 0.125rem;
  
  &:checked {
    background-color: #0d6efd;
    border-color: #0d6efd;
  }
  
  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.form-check-label {
  cursor: pointer;
  margin-left: 0.5rem;
  
  strong {
    color: #495057;
    font-size: 1.1rem;
  }
}

.method-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-left: 1.75rem;
  margin-top: 0.25rem;
}

.alert {
  border-radius: 0.5rem;
  
  i {
    font-size: 1rem;
  }
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.text-muted {
  color: #6c757d !important;
}

.custom-other-input {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  margin-top: 0.75rem;
  
  .form-label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
  }
  
  .form-control {
    border: 1px solid #ced4da;
    
    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }
  
  textarea.form-control {
    resize: vertical;
    min-height: calc(1.5em + 0.75rem + 2px);
  }
}
</style>
